import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, FileBox, Users, LogIn, AlertCircle, LogOut as LogOutIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/common/PageHeader';
import { Loader } from '@/components/common/Loader';
import { EmptyState } from '@/components/common/EmptyState';
import { dashboardsApi } from '@/api/endpoints';
import { formatDate, truncate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

interface Overview {
  generatedAtUtc: string;
  last24h: {
    detectionCheat: number; detectionCheatUnknown?: number;
    detectionDll: number; detectionDllUnknown?: number;
    detectionFiles: number; detectionFilesUnknown?: number;
    sessionAuth: number; authSpoof: number; sessionDisconnect: number;
  };
  allowlist?: { md5Count: number; sha256Count: number };
  last7d: { topCheaters: any[]; topArtifacts: any[]; typeDistribution: any[]; };
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    dashboardsApi.overview().then(setData).catch(e => setErr(e.message));
  }, []);

  if (err) return <EmptyState title="Erro ao carregar" description={err} icon={AlertCircle} />;
  if (!data) return <div className="flex items-center justify-center py-12"><Loader /></div>;

  const kpis = [
    { key: 'detectionsCheat24h', icon: ShieldAlert, value: data.last24h.detectionCheat, unknown: data.last24h.detectionCheatUnknown, tone: 'text-destructive' },
    { key: 'detectionsDll24h',   icon: FileBox,     value: data.last24h.detectionDll,   unknown: data.last24h.detectionDllUnknown, tone: 'text-warning' },
    { key: 'detectionsFiles24h', icon: FileBox,     value: data.last24h.detectionFiles, unknown: data.last24h.detectionFilesUnknown, tone: 'text-warning' },
    { key: 'authAttempts24h',    icon: LogIn,       value: data.last24h.sessionAuth,    unknown: undefined, tone: 'text-info' },
    { key: 'authSpoofs24h',      icon: AlertCircle, value: data.last24h.authSpoof,      unknown: undefined, tone: 'text-destructive' },
    { key: 'disconnects24h',     icon: LogOutIcon,  value: data.last24h.sessionDisconnect, unknown: undefined, tone: 'text-muted-foreground' }
  ];

  const subtitleExtra = data.allowlist
    ? ` • ${data.allowlist.md5Count + data.allowlist.sha256Count} artefatos permitidos (allowlist)`
    : '';

  return (
    <div>
      <PageHeader title={t('dashboard.title')} subtitle={`${t('dashboard.subtitle')} • ${formatDate(data.generatedAtUtc)}${subtitleExtra}`} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {kpis.map(k => {
          const hasUnknown = typeof k.unknown === 'number';
          const filteredOut = hasUnknown && k.value > 0 ? k.value - (k.unknown ?? 0) : 0;
          return (
            <div key={k.key} className="kpi-card" title={hasUnknown ? `${k.value.toLocaleString('pt-BR')} brutas — ${filteredOut.toLocaleString('pt-BR')} já marcadas como permitidas` : undefined}>
              <div className="flex items-start justify-between">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">{t(`dashboard.kpis.${k.key}`)}</div>
                <k.icon className={`h-4 w-4 ${k.tone}`} />
              </div>
              {hasUnknown ? (
                <>
                  <div className={`text-2xl font-bold mt-2 tabular-nums ${(k.unknown ?? 0) > 0 ? k.tone : 'text-muted-foreground'}`}>
                    {(k.unknown ?? 0).toLocaleString('pt-BR')}
                    <span className="text-xs font-normal text-muted-foreground ml-1">não verif.</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {k.value.toLocaleString('pt-BR')} brutas · {filteredOut.toLocaleString('pt-BR')} em allowlist
                  </div>
                </>
              ) : (
                <div className="text-2xl font-bold mt-2 tabular-nums">{k.value.toLocaleString('pt-BR')}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>{t('dashboard.topCheaters')}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.last7d.topCheaters?.length ? (
              <div className="divide-y">
                {data.last7d.topCheaters.slice(0, 10).map((c: any, i: number) => (
                  <Link key={c.hwid || i} to={`/drill?type=hwid&value=${encodeURIComponent(c.hwid || '')}`} className="flex items-center justify-between px-4 py-2 hover:bg-muted/40 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-muted-foreground tabular-nums">#{i + 1}</div>
                      <div className="font-mono text-sm truncate">{truncate(c.hwid, 24)}</div>
                      <div className="text-xs text-muted-foreground">{(c.nicks || []).slice(0, 2).join(' • ') || '—'}</div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <div className="font-bold tabular-nums">{c.totalDetections}</div>
                      <Badge variant="destructive" className="text-[10px]">{c.riskScoreMax || 0}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            ) : <EmptyState />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('dashboard.topArtifacts')}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.last7d.topArtifacts?.length ? (
              <div className="divide-y">
                {data.last7d.topArtifacts.slice(0, 10).map((a: any, i: number) => (
                  <div key={a.hash || i} className="flex items-center justify-between px-4 py-2 hover:bg-muted/40">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-muted-foreground"><Badge variant="outline">{a.hashType}</Badge></div>
                      <div className="font-mono text-xs truncate">{a.hash}</div>
                      <div className="text-xs text-muted-foreground">{truncate(a.modulePathSample, 50)}</div>
                    </div>
                    <div className="font-bold tabular-nums shrink-0 ml-3">{a.count}</div>
                  </div>
                ))}
              </div>
            ) : <EmptyState />}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>{t('dashboard.typeDistribution')}</CardTitle></CardHeader>
          <CardContent>
            {data.last7d.typeDistribution?.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {data.last7d.typeDistribution.map((t: any) => (
                  <div key={t.detectionType} className="border rounded-md p-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{t.detectionType}</div>
                    <div className="text-xl font-bold tabular-nums">{t.count}</div>
                    <div className="text-xs text-muted-foreground">Risk avg: {(t.avgRiskLevel ?? 0).toFixed(1)} • Conf avg: {(t.avgConfidence ?? 0).toFixed(0)}%</div>
                  </div>
                ))}
              </div>
            ) : <EmptyState />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
